/**
 * デザイン成果物横断検索 API クライアント。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイン成果物横断検索 API クライアント。GET /design-search を呼ぶ
 * @graph-connects /design-search [calls] デザイン成果物横断検索 API
 */

import type { DesignSearchParams, DesignSearchResponse } from '../../types/design-search.js';
import { config } from '../../config.js';
import { ApiError, request } from './_request.js';

/** @graph-connects /design-search [calls] デザイン成果物横断検索 API */
export const designSearchApi = {
  /**
   * キーワードで Drive 成果物 + Slack 履歴を横断検索する。
   * @graph-connects /design-search [calls] 検索実行
   */
  search: (params: DesignSearchParams): Promise<DesignSearchResponse> => {
    // q が空でもブラウズ（新しい順）として成立するため、常にリクエストする。
    const q = new URLSearchParams({ q: params.q });
    if (params.caseFolder) q.set('caseFolder', params.caseFolder);
    if (params.year) q.set('year', params.year);
    if (params.month) q.set('month', params.month);
    if (params.type) q.set('type', params.type);
    if (params.limit !== undefined) q.set('limit', String(params.limit));
    return request<DesignSearchResponse>(`/design-search?${q.toString()}`);
  },

  /**
   * 成果物サムネイルの GCS 永続コピーを Cookie 認証付き fetch で取得し Blob を返す。
   *
   * `<img src>` の直参照は Edge Router がサブリソースを非認証扱いにして OAuth へ 302
   * リダイレクトするため、閲覧者のセッション状態次第で壊れる。検索本体と同じ
   * `credentials: 'include'` の fetch で取得することで認証経路に乗せる。認証リダイレクト
   * (HTML) や XSS 対策で octet-stream に正規化された非画像は画像として描画しないため throw する。
   * @graph-connects /design-search/thumbnail [calls] サムネイル GCS プロキシ配信
   */
  fetchThumbnailBlob: async (objectKey: string): Promise<Blob> => {
    const res = await fetch(
      `${config.apiUrl}/design-search/thumbnail?key=${encodeURIComponent(objectKey)}`,
      { credentials: 'include' },
    );
    if (!res.ok) {
      throw new ApiError(`Thumbnail fetch failed: ${String(res.status)}`, res.status);
    }
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) {
      throw new ApiError('Thumbnail response is not an image', res.status);
    }
    return blob;
  },
};
